export type ConnectedPlatformLike = {
  display_name?: string | null
  name?: string | null
  credentials?: {
    country?: string | null
    country_code?: string | null
    country_iso?: string | null
  } | null
  platform_info?: {
    display_name?: string | null
    name?: string | null
    logo_ref?: string | null
  } | null
} | null | undefined

const trimValue = (value?: string | null) => String(value ?? '').trim()

export const getConnectedPlatformLabel = (platform: ConnectedPlatformLike): string =>
  trimValue(platform?.display_name) ||
  trimValue(platform?.platform_info?.display_name) ||
  trimValue(platform?.name) ||
  'Channel'

export const getConnectedPlatformTypeLabel = (platform: ConnectedPlatformLike): string =>
  trimValue(platform?.platform_info?.display_name) || trimValue(platform?.platform_info?.name)

export const getConnectedPlatformMonogram = (platform: ConnectedPlatformLike): string => {
  const source = getConnectedPlatformTypeLabel(platform) || getConnectedPlatformLabel(platform)
  return source.charAt(0).toUpperCase() || 'C'
}

/** Resolve platform logo URL (absolute or API-prefixed relative), same as channel tabs / sidebar. */
export const getConnectedPlatformLogoUrl = (platform: ConnectedPlatformLike): string | null => {
  const raw = trimValue(platform?.platform_info?.logo_ref)
  if (!raw) return null
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:')) {
    return raw
  }
  if (raw.startsWith('//')) return `https:${raw}`
  const apiBase = trimValue(process.env.REACT_APP_API_URL).replace(/\/$/, '')
  if (raw.startsWith('/') && apiBase) return `${apiBase}${raw}`
  return raw
}

const prefetchedLogoUrls = new Set<string>()

/** Warm browser cache for channel logos (listing table / selects). */
export const prefetchConnectedPlatformLogos = (
  platforms: ConnectedPlatformLike[] | null | undefined
): void => {
  if (typeof Image === 'undefined' || !platforms?.length) return
  platforms.forEach((platform) => {
    const url = getConnectedPlatformLogoUrl(platform)
    if (!url || prefetchedLogoUrls.has(url)) return
    prefetchedLogoUrls.add(url)
    const img = new Image()
    img.referrerPolicy = 'no-referrer'
    img.decoding = 'sync'
    img.src = url
  })
}

export const getConnectedPlatformCountryCode = (platform: ConnectedPlatformLike): string => {
  const raw =
    trimValue(platform?.credentials?.country_code) ||
    trimValue(platform?.credentials?.country_iso) ||
    trimValue(platform?.credentials?.country)

  if (!raw) return ''
  if (raw.length === 2 || raw.length === 3) return raw.toLowerCase()

  const match = raw.match(/\b([A-Za-z]{2})\b/)
  return match ? match[1].toLowerCase() : ''
}

export const getConnectedPlatformTooltip = (platform: ConnectedPlatformLike): string => {
  const label = getConnectedPlatformLabel(platform)
  const typeLabel = getConnectedPlatformTypeLabel(platform)
  const country = getConnectedPlatformCountryCode(platform)
  const parts = [label]

  if (typeLabel && typeLabel.toLowerCase() !== label.toLowerCase()) parts.push(typeLabel)
  if (country) parts.push(country.toUpperCase())

  return parts.join(' · ')
}
